import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import CryptoJS from "crypto-js";
import { jwtDecode } from "jwt-decode";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useSnackbar } from "../../contexts/SnackbarContext";
import { login } from "../../services/auth.service";
import { useAuthStore } from "../../stores/store";
import { AuthenStoreType } from "../../types/authen";
import { checkRefreshTokenWithExpire } from "../../utils/auth-fetch";
import SkeletonForm from "./SkeletonForm";

const initialFormData = {
  username: "",
  password: "",
};

const formSchema = z.object({
  username: z.string().min(1, {
    message: "Username is required. Please enter a username.",
  }),
  password: z.string().min(1, {
    message: "Password is required. Please enter a password.",
  }),
});

export default function Form() {
  const navigate = useNavigate();
  const auth = useAuthStore((state) => (state as AuthenStoreType).user);
  const setAuth = useAuthStore(
    (state) => (state as AuthenStoreType).setAuthState,
  );
  const { showSnackbar } = useSnackbar();

  const resolveRedirectPath = useCallback((role?: string) => {
    if (role === "admin") return "/admin";
    if (role === "crm") return "/crm";
    return "/portal";
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [preLoading, setPreLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const ifAuthorized = useCallback(async () => {
    await checkRefreshTokenWithExpire().then(() => {
      if (auth) {
        navigate(resolveRedirectPath(auth.role), { replace: true });
      }
    });
  }, [auth, navigate, resolveRedirectPath]);

  useEffect(() => {
    if (auth) {
      ifAuthorized();
    } else {
      setPreLoading(false);
    }
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const { control, handleSubmit } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialFormData,
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    let body = {
      username: values.username,
      password: CryptoJS.SHA256(values.password).toString(),
    };

    setIsLoading(true);
    try {
      const response = await login(body);

      if (response) {
        let userAuth = response;
        userAuth.expire_at =
          jwtDecode(response.access_token).exp! * 1000 - 15000;
        setAuth(userAuth);
        navigate(resolveRedirectPath(response.role));
      }
    } catch (error) {
      console.error("🚀 ~ error:", error);
      showSnackbar("Login Failed!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (preLoading) {
    return <SkeletonForm />;
  }

  return (
    <>
      <Box
        component="form"
        autoComplete="off"
        p="16px"
        className="w-full max-w-[392px]"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography variant="body1" className="text-xl font-bold mb-2">
          Log in to the Essilor system
        </Typography>
        <div className="flex gap-2 items-center mb-4">
          <div className="text-sm text-gray-400">
            If you need more info, please check out
          </div>
          <div className="text-sm text-info-dark">FAQ Page</div>
        </div>
        <div className="flex flex-col self-stretch gap-3 mb-4">
          <InputLabel htmlFor="username">Username</InputLabel>
          <Controller
            name="username"
            control={control}
            render={({
              field: { ref, onChange, value },
              fieldState: { error },
            }) => (
              <TextField
                placeholder="Username"
                fullWidth
                error={!!error}
                helperText={error?.message}
                onChange={onChange}
                value={value}
                inputRef={ref}
              />
            )}
          />
          <InputLabel htmlFor="password">Password</InputLabel>
          <Controller
            name="password"
            control={control}
            render={({
              field: { ref, onChange, value },
              fieldState: { error },
            }) => (
              <TextField
                placeholder="Password"
                fullWidth
                inputRef={ref}
                type={showPassword ? "text" : "password"}
                error={!!error}
                helperText={error?.message}
                onChange={onChange}
                value={value}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword
                              ? "hide the password"
                              : "display the password"
                          }
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </div>
        <div className="flex md:justify-end">
          <Button
            type="submit"
            variant="contained"
            className="w-full md:w-full md:max-w-[120px]"
            loading={isLoading}
          >
            Login
          </Button>
        </div>
      </Box>
    </>
  );
}
